import { useState, useEffect, useRef } from "react";
import { Save, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../utils/apiClient";
import { MACHINE_STATUSES, STATUS_STYLES, usesKm, usesHours } from "./readingType";

const BulkLogEntryTab = ({ projectId, equipment, onRefresh }) => {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastReadings, setLastReadings] = useState({});
  const [loadingReadings, setLoadingReadings] = useState(false);
  const [readingsError, setReadingsError] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    if (equipment.length > 0) {
      initRows();
      fetchLastReadings();
    }
  }, [equipment, date]);

  const initRows = () => {
    setRows(equipment.filter(eq => eq.status !== "Archived").map(eq => ({
      equipment_id: eq.id, name: eq.name, reg: eq.equipment_id,
      useKm: usesKm(eq), useHr: usesHours(eq), status: "Working",
      km_start: "", km_end: "", km_run: "",
      hour_start: "", hour_end: "", hours_used: "",
      operator_name: "", activity: "", remarks: "", touched: false
    })));
  };

  const fetchLastReadings = async () => {
    const id = ++reqId.current;
    setLoadingReadings(true);
    setReadingsError(false);
    try {
      const res = await apiClient.get(`/equipment/last-log-readings-bulk/${projectId}`, { params: { before: date } });
      if (id !== reqId.current) return;
      const readings = {};
      Object.entries(res.data).forEach(([eqId, v]) => { readings[eqId] = { km: v.closing_km, hour: v.closing_hour, date: v.last_date }; });
      setLastReadings(readings);
      setRows(prev => prev.map(r => {
        const last = readings[r.equipment_id];
        return last ? { ...r, km_start: r.km_start || last.km || "", hour_start: r.hour_start || last.hour || "" } : r;
      }));
    } catch (e) {
      if (id === reqId.current) { setReadingsError(true); toast.error("Failed to load opening readings"); }
    } finally {
      if (id === reqId.current) setLoadingReadings(false);
    }
  };

  const hasData = (r) => r.touched && (r.hours_used || r.activity || r.status !== "Working");

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value, touched: true };
      // Auto-calculate KM Run from KM readings
      if ((field === "km_end" || field === "km_start") && updated[idx].km_end && updated[idx].km_start) {
        const kmDiff = parseFloat(updated[idx].km_end) - parseFloat(updated[idx].km_start);
        if (kmDiff >= 0) updated[idx].km_run = kmDiff.toFixed(1);
      }
      // Auto-calculate Hours Used ONLY from Hour readings (NOT from KM)
      if ((field === "hour_end" || field === "hour_start") && updated[idx].hour_end && updated[idx].hour_start) {
        const hrDiff = parseFloat(updated[idx].hour_end) - parseFloat(updated[idx].hour_start);
        if (hrDiff > 0) updated[idx].hours_used = hrDiff.toFixed(1);
      }
      return updated;
    });
  };

  const handleSaveAll = async () => {
    const toSave = rows.filter(hasData);
    if (toSave.length === 0) {
      toast.error("No data to save. Fill at least Hours Used or Activity.");
      return;
    }
    setSaving(true);
    let saved = 0, failed = 0;
    for (const r of toSave) {
      const idle = r.status !== "Working";
      const kmS = parseFloat(r.km_start) || 0, hrS = parseFloat(r.hour_start) || 0;
      const kmE = parseFloat(r.km_end) || (idle ? kmS : 0), hrE = parseFloat(r.hour_end) || (idle ? hrS : 0);
      try {
        await apiClient.post("/equipment/daily-log", {
          project_id: projectId, equipment_id: r.equipment_id, date,
          km_start_reading: kmS, km_end_reading: kmE, km_used: Math.max(0, kmE - kmS),
          hour_start_reading: hrS, hour_end_reading: hrE,
          hours_used: parseFloat(r.hours_used) || 0,
          start_reading: hrS, end_reading: hrE,
          operator_name: r.operator_name || "N/A", activity: r.activity || (r.status === "Working" ? "General Work" : r.status),
          remarks: r.remarks, machine_status: r.status, fuel_issued: 0, breakdown_hours: 0, breakdown_reason: "",
          performance_metric: 0, performance_unit: "KM/hr", location: ""
        });
        saved++;
      } catch (e) {
        failed++;
        console.error(`Failed to save log for ${r.name}:`, e);
      }
    }
    setSaving(false);
    if (saved > 0) toast.success(`Saved ${saved} daily log(s)`);
    if (failed > 0) toast.error(`Failed to save ${failed} log(s)`);
    if (saved > 0) {
      onRefresh?.();
      initRows();
      fetchLastReadings();
    }
  };

  const inputCls = "w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono";
  const hasWarning = (r) => r.status === "Working" && (
    (r.useKm && r.km_end && r.km_start && parseFloat(r.km_end) < parseFloat(r.km_start)) ||
    (r.useHr && r.hour_end && r.hour_start && parseFloat(r.hour_end) < parseFloat(r.hour_start)));
  const na = <span className="text-[10px] text-slate-400">N/A</span>;

  return (
    <div className="space-y-4" data-testid="bulk-log-entry-tab">
      <div className="stat-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 uppercase tracking-widest">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="bulk-entry-date" />
            </div>
            <div className="text-sm text-slate-600 mt-5">
              <span className="font-medium">{rows.filter(hasData).length}</span> of {rows.length} machines have data
            </div>
          </div>
          <button onClick={handleSaveAll} disabled={saving || loadingReadings || readingsError} className="btn-primary flex items-center gap-2" data-testid="bulk-entry-save-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : loadingReadings ? "Loading readings..." : "Save All Logs"}
          </button>
          {readingsError && (
            <button onClick={fetchLastReadings} className="btn-secondary text-sm" data-testid="bulk-retry-readings-btn">Retry loading readings</button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bulk-entry-table">
            <thead>
              <tr className="border-b-2 border-slate-300 text-[10px] uppercase tracking-widest text-slate-600">
                <th className="py-2 px-2 text-left w-[180px]">Equipment</th>
                <th className="py-2 px-2 text-left w-[130px]">Status</th>
                <th className="py-2 px-2 text-center w-[80px]">KM Open</th>
                <th className="py-2 px-2 text-center w-[80px]">KM Close</th>
                <th className="py-2 px-2 text-center w-[70px]">KM Run</th>
                <th className="py-2 px-2 text-center w-[80px]">Hr Open</th>
                <th className="py-2 px-2 text-center w-[80px]">Hr Close</th>
                <th className="py-2 px-2 text-center w-[70px]">Hours</th>
                <th className="py-2 px-2 text-left w-[120px]">Operator</th>
                <th className="py-2 px-2 text-left w-[150px]">Activity</th>
                <th className="py-2 px-2 text-left">Remarks</th>
                <th className="py-2 px-2 w-[30px]"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.equipment_id} className={`border-b border-slate-200 ${r.touched ? "bg-blue-50/50" : ""}`} data-testid={`bulk-row-${r.equipment_id}`}>
                  <td className="py-2 px-2">
                    <div className="font-medium text-primary truncate" title={r.name}>{r.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.reg}</div>
                  </td>
                  <td className="py-1 px-1">
                    <select value={r.status} onChange={(e) => updateRow(idx, "status", e.target.value)}
                      className={`w-full px-1 py-1.5 border border-slate-300 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-primary ${STATUS_STYLES[r.status]}`}
                      data-testid={`bulk-status-${r.equipment_id}`}>
                      {MACHINE_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                    </select>
                  </td>
                  <td className="py-1 px-1 text-center">{r.useKm ? <input type="number" step="0.1" value={r.km_start} onChange={(e) => updateRow(idx, "km_start", e.target.value)} className={inputCls} placeholder="0" data-testid={`bulk-km-start-${r.equipment_id}`} /> : na}</td>
                  <td className="py-1 px-1 text-center">{r.useKm ? <input type="number" step="0.1" value={r.km_end} onChange={(e) => updateRow(idx, "km_end", e.target.value)} className={inputCls} placeholder={r.status !== "Working" ? (r.km_start || "Optional") : "0"} data-testid={`bulk-km-end-${r.equipment_id}`} /> : na}</td>
                  <td className="py-1 px-1 text-center font-mono text-sm font-medium text-blue-600">{r.useKm ? (r.km_run || '-') : na}</td>
                  <td className="py-1 px-1 text-center">{r.useHr ? <input type="number" step="0.1" value={r.hour_start} onChange={(e) => updateRow(idx, "hour_start", e.target.value)} className={inputCls} placeholder="0" data-testid={`bulk-hr-start-${r.equipment_id}`} /> : na}</td>
                  <td className="py-1 px-1 text-center">{r.useHr ? <input type="number" step="0.1" value={r.hour_end} onChange={(e) => updateRow(idx, "hour_end", e.target.value)} className={inputCls} placeholder={r.status !== "Working" ? (r.hour_start || "Optional") : "0"} data-testid={`bulk-hr-end-${r.equipment_id}`} /> : na}</td>
                  <td className="py-1 px-1"><input type="number" step="0.1" value={r.hours_used} onChange={(e) => updateRow(idx, "hours_used", e.target.value)} className={`${inputCls} font-semibold`} placeholder="0" /></td>
                  <td className="py-1 px-1"><input type="text" value={r.operator_name} onChange={(e) => updateRow(idx, "operator_name", e.target.value)} className={inputCls.replace("font-mono", "")} placeholder="Name" /></td>
                  <td className="py-1 px-1"><input type="text" value={r.activity} onChange={(e) => updateRow(idx, "activity", e.target.value)} className={inputCls.replace("font-mono", "")} placeholder="Work done" /></td>
                  <td className="py-1 px-1"><input type="text" value={r.remarks} onChange={(e) => updateRow(idx, "remarks", e.target.value)} className={inputCls.replace("font-mono", "")} placeholder="Notes" /></td>
                  <td className="py-1 px-1 text-center">
                    {hasWarning(r) && <AlertTriangle className="w-4 h-4 text-amber-500 inline" title="Closing < Opening" />}
                    {!hasWarning(r) && hasData(r) && <CheckCircle className="w-4 h-4 text-green-500 inline" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="text-center py-8 text-slate-500">No equipment found.</div>
        )}
      </div>
    </div>
  );
};

export default BulkLogEntryTab;
